'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { clearCalculatorState } from '@/lib/calculator-storage';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isPasswordProtectionEnabled: boolean; // Kept for backward compatibility
  authEnabled: boolean;
  authMethod?: 'password' | 'email';
  userEmail?: string; // User's email address (only available for magic link auth)
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authEnabled, setAuthEnabled] = useState(false);
  const [authMethod, setAuthMethod] = useState<'password' | 'email' | undefined>(undefined);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication status on mount and handle magic link auth
  useEffect(() => {
    // Check for magic link authentication (indicated by ?authenticated= URL param)
    // This param is only set immediately after magic link redirect
    const urlParams = new URLSearchParams(window.location.search);
    const magicLinkAuth = urlParams.get('authenticated');
    
    if (magicLinkAuth) {
      // Clear calculator state on magic link login
      clearCalculatorState();
      
      // Clean up the URL param to prevent re-triggering on refresh
      const url = new URL(window.location.href);
      url.searchParams.delete('authenticated');
      window.history.replaceState({}, '', url.toString());
    }
    
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/auth/status', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(data.authenticated);
        setAuthEnabled(data.authEnabled);
        setAuthMethod(data.authMethod);
        setUserEmail(data.userEmail); // Only available for magic link auth
      } else {
        setIsAuthenticated(false);
        setAuthEnabled(false);
        setAuthMethod(undefined);
        setUserEmail(undefined);
      }
    } catch (error) {
      console.error('Failed to check auth status:', error);
      setIsAuthenticated(false);
      setAuthEnabled(false);
      setAuthMethod(undefined);
      setUserEmail(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (password: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        // Clear calculator state on successful login
        clearCalculatorState();
        
        setIsAuthenticated(true);
        // Keep sessionStorage for backward compatibility with client-side logic
        sessionStorage.setItem('accessnow-authenticated', 'true');
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsAuthenticated(false);
      setUserEmail(undefined);
      sessionStorage.removeItem('accessnow-authenticated');
    }
  };

  // Don't render anything while checking initial auth state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-accessnow-orange-lighter via-white to-accessnow-teal-light/10 dark:from-neutral-900 dark:via-neutral-800 dark:to-accessnow-orange/20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accessnow-orange"></div>
      </div>
    );
  }

  const value = {
    isAuthenticated,
    login,
    logout,
    isPasswordProtectionEnabled: authEnabled, // Kept for backward compatibility
    authEnabled,
    authMethod,
    userEmail,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}